import { createFileRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { FullScreenLoader } from '@/components/layout/FullScreenLoader';
import { AtlasProvider } from '@/features/atlas';
import { AreaOnboardingDialog } from '@/features/auth/AreaOnboardingDialog';
import { useAuth } from '@/features/auth/auth-context';
import { useMe } from '@/features/auth/use-me';
import { ProductTourHost } from '@/features/metric-hub/onboarding/ProductTourHost';
import { useFirstVisitRedirect } from '@/features/metric-hub/onboarding/use-first-visit-redirect';
import { AtlasManagedQuickPropose } from '@/features/metric-hub/propose/AtlasManagedQuickPropose';

export const Route = createFileRoute('/_authed')({
  component: AuthedLayout,
});

function AuthedLayout() {
  const { user, isLoading } = useAuth();
  const me = useMe();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !user) {
      void navigate({ to: '/sign-in', replace: true });
    }
  }, [user, isLoading, navigate]);

  if (isLoading || !user) {
    return <FullScreenLoader label="Checking your session…" />;
  }

  // Wait for /auth/me before rendering the shell — both the area onboarding
  // dialog (below) and several downstream components depend on it being
  // hydrated. Brief loader keeps the flash imperceptible.
  if (me.isPending || !me.data) {
    return <FullScreenLoader label="Checking your session…" />;
  }

  return (
    <>
      {/* Atlas sidebar lives at the root authed layout so ⌘J / ⌘K
          works on every page (catalog, dashboard, detail, propose,
          drafts, …). The provider is a no-op when
          VITE_ATLAS_ENABLED=false OR the server has no
          MINDSHUB_API_KEY — under those conditions it just returns
          children. Per-page `Apply` receptors register themselves
          via `<AtlasApplyProvider>` deeper in the tree. */}
      <AtlasProvider intent="creator">
        <AppShell>
          <Outlet />
        </AppShell>
      </AtlasProvider>
      {/* Mounted unconditionally — the dialog itself decides whether to open
          based on `me.area`. Renders nothing when an area is already set. */}
      <AreaOnboardingDialog me={me.data} />
      {/* Cross-page product tour host. Renders nothing until a tour is started
          (from onboarding or the command palette); then it walks the user
          through the real Metric Hub screens. Lives here so it survives
          navigation between routes. */}
      <ProductTourHost />
      {/* Hidden dialog Atlas can pop from any /metric-hub page via the
          `open-quick-propose-metric` action kind. Renders nothing until
          a request lands on the bus. */}
      <AtlasManagedQuickPropose />
      {/* First-visit one-shot redirect to /metric-hub/onboarding.
          Mounted only when the user is somewhere under /metric-hub so
          the onboarding-progress query doesn't fire on other parts of
          the app. Renders nothing. */}
      <MetricHubFirstVisitRedirect />
    </>
  );
}

/** Mounts the first-visit redirect hook only when the active route is
 *  under `/metric-hub`. Keeps the onboarding-progress GET scoped to
 *  the surface that cares about it. */
function MetricHubFirstVisitRedirect() {
  const inMetricHub = useRouterState({
    select: (s) => s.location.pathname.startsWith('/metric-hub'),
  });
  if (!inMetricHub) return null;
  return <FirstVisitRedirectRunner />;
}

function FirstVisitRedirectRunner() {
  useFirstVisitRedirect();
  return null;
}
